// Broker Commission Modeler — the tab's vocabulary and its percent boundary.
//
// 🔴 THE WORDING IS NOT THIS FILE'S TO CHOOSE. Every mode label, field label and
// helper sentence below is taken VERBATIM from the real Deal card
// (components/deals/BrokerFeeAuthorizationCard.tsx). A broker who models a deal
// here and then reads their signed authorization there must see one vocabulary,
// not two dialects of it. If the card's copy changes, this changes with it.
//
// Source strings are SENTENCE CASE. The design system uppercases where it wants
// to (PageHeader eyebrows, dt labels) via CSS — typing caps here would double up
// on some surfaces and disagree with the card on the rest.
//
// The one deliberate DIFFERENCE from the card: there is no verification step
// here, so none of "Verified" / "Not yet verified" / "Confirm terms" is ported.
// A model is a scratchpad; nothing signs it.

import { formatCurrency } from "@/design-system/lib/format";
import { PortalApiError } from "../../api/client";
import type {
  BrokerCashAtClosingMode,
  BrokerFeeMonthlyMode,
} from "../../api/commissionModeler";
import type { CommissionModelerFormCopy } from "../../api/commissionModelerCopy";

/** The three MONTHLY arrangements, in the card's order. */
export const MODE_LABEL: Record<BrokerFeeMonthlyMode, string> = {
  none: "No monthly fee",
  percent_of_seller_share: "% of seller's monthly share",
  flat_monthly: "Flat monthly amount",
};

export const MODE_ORDER: BrokerFeeMonthlyMode[] = [
  "none",
  "percent_of_seller_share",
  "flat_monthly",
];

/** The three CASH-AT-CLOSING arrangements, keyed by their BrokerCashAtClosingMode
 *  wire values.
 *
 *  🔴 A separate vocabulary from MODE_LABEL above, deliberately. The two mode sets
 *  are separate types because the monthly one gates a money path and this one gates
 *  nothing, and their percentages are taken on different bases — the seller's
 *  monthly share up there, the buyer's down payment here. One label serving both
 *  would leave a broker unable to tell which basis they had picked. */
export const CASH_MODE_LABEL: Record<BrokerCashAtClosingMode, string> = {
  none: "No cash at closing",
  flat: "Flat amount",
  percent_of_down_payment: "% of down payment",
};

export const CASH_MODE_ORDER: BrokerCashAtClosingMode[] = [
  "none",
  "flat",
  "percent_of_down_payment",
];

/** Field labels + helper text, verbatim from the card's edit modal. */
export const FIELD_COPY = {
  percentLabel: "Percent of seller's monthly share",
  percentHelper: "Entered as a percent (e.g. 5 for 5%); must be under 100%.",
  flatLabel: "Flat monthly amount (USD)",
  // "Broker cash at closing", not "Cash at closing". The deal-structure field of
  // that name (Deal.CashAtClosing) is the BUYER's contribution and renders
  // unqualified on the deal Overview tab; two meanings on one record, so the broker
  // side carries the qualifier. The helper no longer says "leave blank if none" —
  // declining is an explicit option in the selector now, not an empty box.
  cashLabel: "Broker cash at closing (USD)",
  cashHelper:
    "Informational — paid directly seller → broker. Not administered by Phoenix.",
  cashPercentLabel: "Percent of down payment",
  cashPercentHelper: "Entered as a percent (e.g. 10 for 10%); must be under 100%.",
  saleLabel: "Sale fee (%)",
  saleHelper:
    "Optional — investor-phase, not yet active. Entered as a percent; must be under 100%.",
} as const;

// ── The admin overlay ───────────────────────────────────────────────────────
//
// The strings above are now ALSO registered server-side (the `commission-modeler`
// document key), so leadership can retype one without a deploy. This is the
// compiled-in default the surface starts from and falls back to.
//
// Every field is a real sentence on the FIRST frame, replaced when the server's
// copy lands. There is no null to guard and no loading branch — the useFunnelCopy()
// contract, verbatim. A block the server publishes blank is passed through, and
// the form drops that label or helper element while keeping its control (D4).
// An empty override table resolves to exactly these words, so a store nobody has
// published to is byte-identical to today.
//
// 🔴 Held byte-for-byte against the server's defaults by
// tests/fixtures/commission-modeler-copy.json — read by BOTH
// CommissionModelerCopyParityTests.cs and copyParity.test.ts, because nothing
// imports across the language boundary. A one-sided edit fails on whichever side
// was not edited.

export const COMMISSION_MODELER_COPY_FALLBACK: CommissionModelerFormCopy = {
  modeNone: MODE_LABEL.none,
  modePercentOfSellerShare: MODE_LABEL.percent_of_seller_share,
  modeFlatMonthly: MODE_LABEL.flat_monthly,
  percentLabel: FIELD_COPY.percentLabel,
  percentHelper: FIELD_COPY.percentHelper,
  flatLabel: FIELD_COPY.flatLabel,
  cashModeNone: CASH_MODE_LABEL.none,
  cashModeFlat: CASH_MODE_LABEL.flat,
  cashModePercentOfDownPayment: CASH_MODE_LABEL.percent_of_down_payment,
  cashLabel: FIELD_COPY.cashLabel,
  cashHelper: FIELD_COPY.cashHelper,
  cashPercentLabel: FIELD_COPY.cashPercentLabel,
  cashPercentHelper: FIELD_COPY.cashPercentHelper,
  saleLabel: FIELD_COPY.saleLabel,
  saleHelper: FIELD_COPY.saleHelper,
};

/** The resolved label for one arrangement. The wire is flat (one field per mode)
 *  while the form iterates MODE_ORDER, so this is the one place the two shapes
 *  meet — exhaustive over the union, so a fourth mode is a typecheck failure here
 *  rather than a blank option in the picker. */
export function modeLabelOf(
  copy: CommissionModelerFormCopy,
  mode: BrokerFeeMonthlyMode,
): string {
  switch (mode) {
    case "none":
      return copy.modeNone;
    case "percent_of_seller_share":
      return copy.modePercentOfSellerShare;
    case "flat_monthly":
      return copy.modeFlatMonthly;
  }
}

/** The resolved label for one CASH arrangement. Twin of `modeLabelOf` above, and
 *  separate for the same reason the two unions are separate — exhaustive over its
 *  own union, so a fourth cash mode is a typecheck failure here rather than a
 *  blank option in the picker. */
export function cashModeLabelOf(
  copy: CommissionModelerFormCopy,
  mode: BrokerCashAtClosingMode,
): string {
  switch (mode) {
    case "none":
      return copy.cashModeNone;
    case "flat":
      return copy.cashModeFlat;
    case "percent_of_down_payment":
      return copy.cashModePercentOfDownPayment;
  }
}

// ── The percent boundary ────────────────────────────────────────────────────
// The server stores FRACTIONS; the form works in whole percents. Both hops are
// guarded with .toFixed(4) — copied from the card, where it exists because
// 0.05 * 100 is 5.000000000000001 in binary floating point and a broker should
// never be shown "5.000000000000001%" or, worse, save it back.

/** FRACTION → percent NUMBER. 0.05 → 5, 0.025 → 2.5. */
export function fractionToPercent(fraction: number): number {
  return Number((fraction * 100).toFixed(4));
}

/** percent NUMBER → FRACTION. 5 → 0.05. */
export function percentToFraction(percent: number): number {
  return Number((percent / 100).toFixed(6));
}

/** Render a stored FRACTION (0.05) as a percent string ("5%"). Trims trailing
 *  zeros so 0.025 → "2.5%" and 0.05 → "5%". */
export function formatPercent(fraction: number): string {
  return `${fractionToPercent(fraction)}%`;
}

/** The monthly fee component — the only money-driving one. Flat mode reads
 *  "$1,500/month", matching the card. */
export function monthlyFeeDisplay(
  mode: BrokerFeeMonthlyMode,
  amount: number,
): string {
  switch (mode) {
    case "percent_of_seller_share":
      return `${formatPercent(amount)} of seller's monthly share`;
    case "flat_monthly":
      return `${formatCurrency(amount)}/month`;
    case "none":
    default:
      return "No monthly fee";
  }
}

/** Money with cents. The shared formatCurrency drops them by default, which is
 *  right for a headline and wrong for a schedule row a reader is expected to add
 *  up — the server quantizes to the cent every month precisely so they can. */
export function formatMoney(amount: number): string {
  return formatCurrency(amount, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
}

/** The cash-at-closing figure in words. Flat and none render the resolved dollar
 *  alone, exactly as before the mode existed; percent mode renders its DERIVATION
 *  first and then the same dollar — "10% of down payment — $20,000.00".
 *
 *  🔴 Held BYTE-IDENTICAL to the server's `CommissionReportModelBuilder
 *  .CashAtClosingDisplay`, down to the em dash (U+2014) and the cents. The PDF is
 *  generated from the same model this screen is rendering, and a broker who reads
 *  "10% of down payment — $20,000" here and "$20,000.00" there has to work out for
 *  themselves whether they are the same figure. `formatPercent` and `formatMoney`
 *  are the twins of the builder's `Percent` and `Money` for the same reason.
 *
 *  Percent mode alone is special-cased because it is the only mode whose dollar a
 *  reader cannot otherwise check: flat cash is a number the broker typed, but a
 *  percentage of the down payment is arithmetic. `resolved` is always the SERVER'S
 *  `result.brokerCashAtClosing` — this function describes that number, it never
 *  computes one. An unknown or absent mode reads as the bare dollar, matching the
 *  builder's own fallback arm. */
export function cashAtClosingDisplay(
  mode: BrokerCashAtClosingMode | undefined,
  percent: number | null | undefined,
  resolved: number,
): string {
  return mode === "percent_of_down_payment"
    ? `${formatPercent(percent ?? 0)} of down payment — ${formatMoney(resolved)}`
    : formatMoney(resolved);
}

export function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof PortalApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

// ── The wizard ──────────────────────────────────────────────────────────────
//
// Five stops: four that ask for something and one that only shows what was
// asked. The split is by QUESTION, not by field count — "what is the deal worth"
// and "what do you earn monthly" are different questions a broker answers with
// different parts of their brain, and the old single-column form made them look
// like one long list of nine inputs.
//
// These strings are COMPILED IN, unlike the ten field labels above. That is
// deliberate and it is the same order the field labels arrived in: prose earns
// its way into the admin overlay by being the kind of sentence leadership
// actually retypes. Step headings are navigation, not vocabulary — the words a
// broker must see match the Deal card are all in FIELD_COPY, already overlaid.
// The two worked examples are the candidates to promote next; they are
// deliberately written as pure functions of the preview so promoting them is a
// token-substitution change and not a rewrite.

export const STEP_KEYS = ["deal", "figures", "monthly", "other", "review"] as const;
export type CommissionWizardStep = (typeof STEP_KEYS)[number];

export const STEP_COPY: Record<
  CommissionWizardStep,
  { label: string; title: string; blurb: string }
> = {
  deal: {
    label: "Deal",
    title: "Which deal is this?",
    blurb:
      "Just so you can tell your scenarios apart later. Neither field changes a number.",
  },
  figures: {
    label: "Figures",
    title: "What is the deal worth?",
    blurb:
      "The total price and what the buyer brings. Whatever is left becomes the seller note — and the note is what every commission below is computed against.",
  },
  monthly: {
    label: "Monthly fee",
    title: "What do you earn each month?",
    blurb:
      "Your ongoing commission, taken from the seller's monthly share. This is the only component that is paid on Phoenix's rails.",
  },
  other: {
    label: "Other",
    title: "Anything at closing or on a sale?",
    blurb:
      "Both are optional. Cash at closing is paid seller to broker directly; the sale fee is investor-phase and not yet active.",
  },
  review: {
    label: "Review",
    title: "Here is the whole picture",
    blurb:
      "Nothing is submitted to Phoenix or attached to a deal. Save it and it is yours to compare against the next one.",
  },
};

/** Step 3's worked example, in the broker's own figures.
 *
 *  Returns null when there is nothing honest to say yet — no preview, or a
 *  no-fee arrangement where the sentence would be a tautology. A worked example
 *  that invents a number to have something to show is worse than no example. */
export function monthlyWorkedExample(
  mode: BrokerFeeMonthlyMode,
  result: {
    sellerReceipt: number;
    brokerMonthlyCommission: number;
    ongoingTotal: number;
    horizonMonths: number;
  } | null,
): string | null {
  if (!result) return null;
  if (mode === "none") return null;
  if (result.brokerMonthlyCommission <= 0) return null;

  const monthly = formatMoney(result.brokerMonthlyCommission);
  const total = formatMoney(result.ongoingTotal);
  const months = result.horizonMonths;

  if (mode === "percent_of_seller_share") {
    return (
      `The seller receives about ${formatMoney(result.sellerReceipt)} a month, so your ` +
      `share of it is about ${monthly} a month — ${total} across the ${months} months ` +
      `modeled.`
    );
  }
  return (
    `${monthly} a month for ${months} months is ${total} — it does not move with the ` +
    `note, so it is the same whether the deal is large or small.`
  );
}

/** Step 4's worked example: the three components added up, each named. Returns
 *  null until there is a lifetime figure to break down. */
export function lifetimeWorkedExample(result: {
  brokerCashAtClosing: number;
  ongoingTotal: number;
  lifetimeTotal: number;
} | null): string | null {
  if (!result || result.lifetimeTotal <= 0) return null;

  const parts: string[] = [];
  if (result.brokerCashAtClosing > 0)
    parts.push(`${formatMoney(result.brokerCashAtClosing)} at closing`);
  if (result.ongoingTotal > 0) parts.push(`${formatMoney(result.ongoingTotal)} monthly`);

  if (parts.length === 0) return null;
  if (parts.length === 1)
    return `That is ${parts[0]} — ${formatMoney(result.lifetimeTotal)} in total.`;

  const last = parts.pop()!;
  return `${parts.join(", ")} and ${last} comes to ${formatMoney(result.lifetimeTotal)}.`;
}

/** Native select — the shared kit has no Select, and @/components/ui/Select is
 *  staff-side. These are the Input tokens (the PrequalificationTab precedent). */
export const SELECT_CLASS =
  "w-full h-10 px-space-3 py-[10px] font-body text-body sm:text-small text-onyx bg-white-pure " +
  "border border-border-light rounded-md transition-colors duration-fast ease-standard " +
  "focus:outline-none focus:border-gold focus:shadow-goldRing " +
  "disabled:bg-white-warm disabled:opacity-60 disabled:cursor-not-allowed";
